/**
 * Shared Edge Function API client
 * Standardises timeouts (AbortController), retry policy, and error objects.
 *
 * RETRY STRATEGY: edgeFn owns all retries. React Query retry MUST be false
 * for any query that uses edgeFn, to avoid double-retry (max 2 calls total).
 *
 * Usage:
 *   const data = await edgeFn<MyResponse>('trip-plan', { body: { destination: 'Oslo' } });
 */
import { supabase } from '@/integrations/supabase/client';
import { resolveTranslation } from '@/contexts/LanguageContext';
import { getAppLanguage } from '@/lib/appLanguage';
import { getAnonAuthHeaders } from '@/lib/anonProof';

// ---------- Per-endpoint default timeouts ----------
const ENDPOINT_TIMEOUTS: Record<string, number> = {
  'stops-autocomplete': 6_000,
  'vehicle-positions': 8_000,
  'stops-nearest': 6_000,
  'stop-departures': 6_000,
  'trip-plan': 15_000,
  'popular-routes': 10_000,
  'flights-avinor': 15_000,
  'service-alerts': 8_000,
  'ferry-cameras': 10_000,
  'queue-prediction': 8_000,
};

const DEFAULT_TIMEOUT = 15_000;

// ---------- Performance warn threshold ----------
const SLOW_THRESHOLD_MS = 2_000;

// ---------- Rate guard (warn only) ----------
const callLog = new Map<string, number[]>();
const RATE_WINDOW_MS = 2_000;
const RATE_MAX_CALLS = 5;

function rateGuardWarn(endpoint: string, requestId: string) {
  const now = Date.now();
  const timestamps = callLog.get(endpoint) ?? [];
  // Prune old entries
  const recent = timestamps.filter(t => now - t < RATE_WINDOW_MS);
  recent.push(now);
  callLog.set(endpoint, recent);

  if (recent.length > RATE_MAX_CALLS) {
    console.warn(
      `[edgeFn rate-guard] "${endpoint}" called ${recent.length}× in ${RATE_WINDOW_MS}ms (requestId: ${requestId})`,
    );
  }
}

// ---------- Request ID generator ----------
function genRequestId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${ts}-${rand}`;
}

// ---------- Error type ----------
export class EdgeFnError extends Error {
  code: string;
  details?: unknown;
  status?: number;
  requestId?: string;

  constructor(opts: { code: string; message: string; details?: unknown; status?: number; requestId?: string }) {
    super(opts.message);
    this.name = 'EdgeFnError';
    this.code = opts.code;
    this.details = opts.details;
    this.status = opts.status;
    this.requestId = opts.requestId;
  }
}

interface EdgeFnEnvelope {
  ok?: boolean;
  data?: unknown;
  error?: { code?: string; message?: string; details?: unknown };
  requestId?: string;
}

function asEdgeFnEnvelope(data: unknown): EdgeFnEnvelope | null {
  if (data && typeof data === 'object' && 'ok' in data) {
    return data as EdgeFnEnvelope;
  }
  return null;
}

// ---------- Options ----------
interface EdgeFnOptions {
  body?: Record<string, unknown>;
  /** HTTP method – default POST */
  method?: 'POST' | 'GET' | 'DELETE' | 'PATCH';
  /** Query params appended to URL (used with GET) */
  query?: Record<string, string>;
  /** Timeout in ms – uses per-endpoint default if not set */
  timeout?: number;
  /** Max retries on network / 5xx errors – default 1 (max 2 calls total) */
  retries?: number;
  /** External AbortSignal (e.g. from React effect cleanup) */
  signal?: AbortSignal;
  /** Extra HTTP headers (only honoured for GET / DELETE / PATCH paths) */
  headers?: Record<string, string>;
  /**
   * Attach the HMAC-bound anonymous identity headers (x-anon-id / x-anon-proof).
   * Opt-in: only endpoints whose CORS allow-list includes these headers may use it.
   */
  anonAuth?: boolean;
}

// ---------- Helpers ----------
const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

const OFFLINE_NETWORK_MESSAGE = /failed to fetch|networkerror|load failed/i;

const isRetryable = (err: unknown): boolean => {
  if (err instanceof EdgeFnError && err.code === 'TIMEOUT') return false;
  if (err instanceof EdgeFnError && err.code === 'ABORTED') return false;
  if (err instanceof EdgeFnError && err.code === 'RATE_LIMITED') return false;
  // No HTTP status + network message = never reached server — fail fast (don't retry offline storm)
  if (
    err instanceof EdgeFnError
    && err.status === undefined
    && OFFLINE_NETWORK_MESSAGE.test(err.message)
  ) {
    return false;
  }
  if (err instanceof EdgeFnError) {
    if (err.status && err.status >= 400 && err.status < 500) return false;
  }
  return true;
};

/**
 * Bearer token for GET/PATCH/DELETE fetch paths. Uses supabase.auth.getSession()
 * at call time (no AuthContext import — avoids circular deps). The client is
 * configured with autoRefreshToken; getSession returns a refreshed access_token
 * when the stored one is expired.
 */
async function resolveAuthHeadersForFetch(): Promise<Record<string, string>> {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session?.access_token) return {};
    return { Authorization: `Bearer ${data.session.access_token}` };
  } catch {
    return {};
  }
}

// ---------- Main function ----------
export async function edgeFn<T = unknown>(
  functionName: string,
  opts: EdgeFnOptions = {},
): Promise<T> {
  const resolvedTimeout = opts.timeout ?? ENDPOINT_TIMEOUTS[functionName] ?? DEFAULT_TIMEOUT;
  const { body, retries = 1, signal, method = 'POST', query, headers: extraHeaders, anonAuth } = opts;
  const isGet = method === 'GET';
  const requestId = genRequestId();
  const startTime = performance.now();

  // Rate guard (warn only, never blocks)
  rateGuardWarn(functionName, requestId);

  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), resolvedTimeout);

    const onExternalAbort = () => ac.abort();
    signal?.addEventListener('abort', onExternalAbort, { once: true });

    try {
      if (signal?.aborted) {
        throw new EdgeFnError({ code: 'ABORTED', message: 'Request aborted', requestId });
      }

      // GET/DELETE use fetch directly; POST uses supabase.functions.invoke
      let data: unknown;
      let error: { message: string } | null = null;

      if (isGet || method === 'DELETE' || method === 'PATCH') {
        const baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
        const qs = query ? '?' + new URLSearchParams(query).toString() : '';
        const authHeaders = await resolveAuthHeadersForFetch();
        const anonHeaders = anonAuth ? await getAnonAuthHeaders() : {};
        const res = await fetch(`${baseUrl}${qs}`, {
          method,
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Content-Type': 'application/json',
            ...(extraHeaders ?? {}),
            ...anonHeaders,
            ...authHeaders,
          },
          signal: ac.signal,
          ...((method === 'DELETE' || method === 'PATCH') && body ? { body: JSON.stringify(body) } : {}),
        });
        const text = await res.text();
        if (!res.ok) {
          // Preserve HTTP status so callers can distinguish 401/403/etc.
          throw new EdgeFnError({
            code: res.status === 401 ? 'UNAUTHORIZED' : res.status === 403 ? 'FORBIDDEN' : 'EDGE_FN_ERROR',
            message: text || `HTTP ${res.status}`,
            status: res.status,
            requestId,
          });
        } else {
          try { data = JSON.parse(text); } catch { data = text; }
        }
      } else {
        const invokeHeaders = anonAuth ? await getAnonAuthHeaders() : undefined;
        const result = await supabase.functions.invoke(functionName, {
          body: body ?? {},
          ...(invokeHeaders ? { headers: invokeHeaders } : {}),
        });
        data = result.data;
        error = result.error;
      }

      clearTimeout(timer);
      signal?.removeEventListener('abort', onExternalAbort);

      // Detect 429 / rate-limited from error message or context
      if (error) {
        const msg = error.message || '';
        const is429 = msg.includes('429') || msg.toLowerCase().includes('rate limit');
        if (is429) {
          throw new EdgeFnError({
            code: 'RATE_LIMITED',
            message: resolveTranslation(getAppLanguage(), 'edge_rate_limited'),
            status: 429,
            requestId,
          });
        }

        throw new EdgeFnError({
          code: 'EDGE_FN_ERROR',
          message: error.message || `Edge function "${functionName}" failed`,
          details: error,
          requestId,
        });
      }

      // Performance logging
      const durationMs = Math.round(performance.now() - startTime);
      if (durationMs > SLOW_THRESHOLD_MS) {
        console.warn(`[edgeFn slow] "${functionName}" took ${durationMs}ms (requestId: ${requestId})`);
      }

      const envelope = asEdgeFnEnvelope(data);
      if (envelope) {
        if (envelope.ok === false && envelope.error) {
          const envErr = envelope.error;
          throw new EdgeFnError({
            code: envErr.code || 'EDGE_FN_ERROR',
            message: envErr.message || 'Unknown error',
            details: envErr.details,
            requestId: envelope.requestId || requestId,
          });
        }
        // ok: true → return data.data (unwrap envelope)
        return envelope.data as T;
      }

      // Legacy non-envelope response (backwards compat)
      return data as T;
    } catch (err) {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onExternalAbort);

      lastError = err;

      if (
        (err instanceof DOMException && err.name === 'AbortError') ||
        (signal?.aborted) ||
        !isRetryable(err) ||
        attempt >= retries
      ) {
        break;
      }

      // Log retry with requestId
      console.warn(`[edgeFn] Retrying "${functionName}" (attempt ${attempt + 1}, requestId: ${requestId})`);
      await wait(500 * (attempt + 1));
    }
  }

  // Performance logging (even on failure)
  const durationMs = Math.round(performance.now() - startTime);
  if (durationMs > SLOW_THRESHOLD_MS) {
    console.warn(`[edgeFn slow] "${functionName}" failed after ${durationMs}ms (requestId: ${lastError instanceof EdgeFnError ? lastError.requestId : requestId})`);
  }

  // Wrap non-EdgeFnError errors
  if (lastError instanceof EdgeFnError) {
    lastError.requestId = requestId;
    throw lastError;
  }

  if (lastError instanceof DOMException && lastError.name === 'AbortError') {
    throw new EdgeFnError({
      code: 'TIMEOUT',
      message: resolveTranslation(getAppLanguage(), 'edge_timeout'),
      requestId,
    });
  }

  throw new EdgeFnError({
    code: 'UNKNOWN',
    message: lastError instanceof Error ? lastError.message : 'Ukjent feil',
    details: lastError,
    requestId,
  });
}

// ---------- Recommended staleTime per endpoint ----------
export const QUERY_STALE_TIMES: Record<string, number> = {
  'stops-nearest': 30_000,
  'stop-departures': 30_000,
  'trip-plan': 60_000,
  'vehicle-positions': 10_000,
  'popular-routes': 60_000,
  'stops-autocomplete': 15_000,
  'service-alerts': 60_000,
  'ferry-cameras': 60_000,
};
